// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.
/**
 * Vault path rules (PLAN.md Appendix B.2). Must give the same results as the Go package
 * server/internal/vaultpath; both run server/test/conformance/fixtures/paths.json.
 */
import { FOLD_MIN } from "./casefold.gen.js";
export const MAX_PATH_BYTES = 1024;
export const MAX_SEGMENT_BYTES = 255;
export class InvalidPathError extends Error {
    path;
    code = "invalid_path";
    constructor(path) {
        super(`invalid path: ${JSON.stringify(path)}`);
        this.path = path;
        this.name = "InvalidPathError";
    }
}
// Control characters are forbidden on purpose (Appendix B.2 rule 4).
// eslint-disable-next-line no-control-regex
const FORBIDDEN = /[\\:*?"<>|\u0000-\u001f\u007f]/u;
const IGNORED_NAMES = new Set([".ds_store", "thumbs.db", "desktop.ini"]);
const IGNORED_FOLDERS = new Set([".git", ".trash"]);
/** UTF-8 length in bytes, without platform APIs (works in browsers, Node, and Hermes). */
function byteLength(s) {
    let n = 0;
    for (const ch of s) {
        const cp = ch.codePointAt(0) ?? 0;
        n += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return n;
}
function isReservedDevice(segment) {
    let base = segment.toLowerCase();
    const dot = base.indexOf(".");
    if (dot >= 0)
        base = base.slice(0, dot);
    if (base === "con" || base === "prn" || base === "aux" || base === "nul")
        return true;
    return /^(com|lpt)[1-9]$/.test(base);
}
// A lone surrogate means the string is not valid Unicode (Go rejects invalid UTF-8 likewise).
// String.prototype.isWellFormed is avoided: not every JavaScript engine (Hermes) has it.
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u;
/** Validates a path and returns its NFC form, or throws InvalidPathError. */
export function normalizePath(input) {
    if (LONE_SURROGATE.test(input))
        throw new InvalidPathError(input);
    const p = input.normalize("NFC");
    if (p === "" || byteLength(p) > MAX_PATH_BYTES || p.startsWith("/") || p.endsWith("/")) {
        throw new InvalidPathError(input);
    }
    const segments = p.split("/");
    segments.forEach((seg, i) => {
        if (seg === "" || seg === "." || seg === ".." || byteLength(seg) > MAX_SEGMENT_BYTES) {
            throw new InvalidPathError(input);
        }
        if (FORBIDDEN.test(seg) || seg.endsWith(" ") || seg.endsWith("."))
            throw new InvalidPathError(input);
        if (isReservedDevice(seg))
            throw new InvalidPathError(input);
        const lower = seg.toLowerCase();
        const last = i === segments.length - 1;
        if ((last && IGNORED_NAMES.has(lower)) || (!last && IGNORED_FOLDERS.has(lower)) || seg.startsWith("~$")) {
            throw new InvalidPathError(input);
        }
    });
    return p;
}
/** Returns the normalized path, or null when the path breaks the rules. */
export function tryNormalizePath(input) {
    try {
        return normalizePath(input);
    }
    catch {
        return null;
    }
}
const foldMin = new Map(FOLD_MIN);
/**
 * The uniqueness key of a normalized path: every character replaced by the smallest code
 * point of its simple case folding orbit (Appendix B.2 rule 5).
 */
export function pathKey(normalized) {
    let out = "";
    for (const ch of normalized) {
        const cp = ch.codePointAt(0) ?? 0;
        out += String.fromCodePoint(foldMin.get(cp) ?? cp);
    }
    return out;
}
/** Whether two normalized paths name the same file. */
export function samePath(a, b) {
    return pathKey(a) === pathKey(b);
}
/** Whether a normalized path is in the Granite settings folder. */
export function isReservedPath(normalized) {
    return pathKey(normalized).startsWith(pathKey(".granite/"));
}
const TEXT_EXTS = new Set([".txt", ".canvas", ".csv", ".json", ".css", ".js", ".yaml", ".yml"]);
/** File kind from the extension (Appendix B.2). */
export function fileKind(path) {
    const ext = extname(path).toLowerCase();
    if (ext === ".md" || ext === ".markdown")
        return "markdown";
    if (TEXT_EXTS.has(ext))
        return "text";
    return "binary";
}
/** The extension including the dot, as Go's path.Ext: "" when there is none. */
export function extname(path) {
    const base = basename(path);
    const dot = base.lastIndexOf(".");
    return dot >= 0 ? base.slice(dot) : "";
}
export function basename(path) {
    const slash = path.lastIndexOf("/");
    return slash >= 0 ? path.slice(slash + 1) : path;
}
export function dirname(path) {
    const slash = path.lastIndexOf("/");
    return slash >= 0 ? path.slice(0, slash) : "";
}
//# sourceMappingURL=path.js.map